import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SimpleForm, required, useTranslate } from 'react-admin';
import { useFormState } from 'react-final-form';
import { useLocation } from 'react-router-dom';
import { Edit } from '../../../base/components/ra/views';
import StringInput from '../../../base/components/customized/common-input/StringInput';
import GroupSelector from '../../../base/components/customized/common-input/GroupSelector';
import BrandInput from './components/BrandInput';
import UrlInput from '../../../base/components/customized/common-input/UrlInput';
import { EnumViewType, registerViewType } from '../../../base/hooks/useViewType';
import { NavigationContext } from '../../../App';
import { CUSTOM_ROUTES, REVERSE_INTEGRATION_OPERATORS } from '../../../constant/customRoutes';
import { UUID_REGEX } from '../../../constant';

const requiredValidator = required();

const OperatorEdit = ({
  resource, ...otherProps
}) => {
  const translate = useTranslate();
  const { pathname } = useLocation();

  const { setNavigationData } = useContext(NavigationContext);

  useEffect(() => {
    setNavigationData({
      pageTitleInfo: {
        label: translate('resources.reverse-integration.operator.name'),
      },
      breadcrumbs: [
        {
          label: translate('ra.page.home'),
          link: '/',
        },
        {
          label: translate('resources.group.system-setup.name'),
        },
        {
          label: translate('resources.reverse-integration.name'),
          link: '/reverse-integration',
        },
        {
          label: REVERSE_INTEGRATION_OPERATORS.QTECH.name,
          link: CUSTOM_ROUTES.QTECH_OPERATOR,
        },
        {
          label: `#${pathname.split('/').find(path => path.match(UUID_REGEX)).slice(0, 4)}`,
          isActive: true,
        },
      ],
    });
    return () => {
      setNavigationData({});
    };
  }, []);

  useEffect(() => {
    // Hotfix: To self register view type because the current system doesn't cover this case
    registerViewType(pathname, EnumViewType.EDIT);
  }, []);

  return (
    <Edit
      resource={resource}
      confirmDialogInfo={{
        content: 'resources.reverse-integration.operator.confirmToEditContent',
      }}
      translatedResourceName={translate('resources.reverse-integration.operator.name')}
      {...otherProps}
    >
      <SimpleForm redirect="show">
        <GroupSelector
          disabled
          source="groupId"
          validate={requiredValidator}
          fullWidth
          variant="outlined"
          inputProps={{
            helperText: true,
            resettable: false,
          }}
        />
        <BrandSelector
          source="brandId"
          fullWidth
          variant="outlined"
          inputProps={{
            helperText: true,
            resettable: false,
          }}
        />
        <StringInput
          source="name"
          validate={requiredValidator}
          fullWidth
          helperText
        />
        <StringInput
          source="operatorId"
          validate={requiredValidator}
          fullWidth
          helperText
          label="resources.reverse-integration.operator.fields.operatorId"
        />
        <UrlInput
          source="authorizeUrl"
          validate={requiredValidator}
          fullWidth
          helperText
        />
        <UrlInput
          source="placeBetUrl"
          label="common.fields.placeBetUrl"
          validate={requiredValidator}
          fullWidth
          helperText
        />
        <UrlInput
          source="payOutUrl"
          validate={requiredValidator}
          fullWidth
          helperText
        />
        <UrlInput
          source="getBalanceUrl"
          validate={requiredValidator}
          fullWidth
          helperText
        />
        <UrlInput
          source="cancelBetUrl"
          validate={requiredValidator}
          fullWidth
          helperText
        />
      </SimpleForm>
    </Edit>
  );
};

const BrandSelector = props => {
  const { values: { groupId } } = useFormState();

  return (
    <BrandInput
      groupId={groupId}
      validate={requiredValidator}
      disabled
      {...props}
    />
  );
};

OperatorEdit.propTypes = {
  resource: PropTypes.string.isRequired,
  options: PropTypes.shape({
    operatorId: PropTypes.string.isRequired,
  }).isRequired,
};

export default OperatorEdit;
